/* ==========================================================================
   assets/kiosk.js
   WCD Medical kiosk lockdown.

   Handles: token gate, back navigation trap, internal only links,
   gesture suppression, idle reset, setup hint.

   This file must contain JavaScript only. A stray CSS rule anywhere in here
   is a parse error, and a parse error stops the entire file from running,
   which silently disables the token gate and the idle timer.

   CHANGES IN THIS REVISION
   ------------------------
   1. Back navigation. The trap used to block every popstate, which also broke
      the kiosk's own back chevron whenever it went through history.back().
      There is now one sanctioned way back: kioskBack(), which sets a flag the
      popstate handler honours exactly once. Anything that did not go through
      kioskBack() is still pushed back onto the stack, so a customer swiping
      out of the kiosk is blocked as before.

      Markup can use either form:
        <a href="/pages/kiosk-shop1" data-kiosk-back>Back</a>
        <button type="button" data-kiosk-back>Back</button>

      With an href, the click becomes a normal in-kiosk navigation, which is
      the more reliable option: after an idle reset or a replaceState the
      history stack is not necessarily where you assume it is. Without an
      href, it falls back to a guarded history.back().

   2. Token storage falls back to sessionStorage, and the kt parameter is only
      stripped from the URL once something has actually persisted it. The old
      code swallowed a failed localStorage write and stripped the token
      anyway, so every later navigation failed the gate and bounced home with
      no visible cause.

   3. A failed gate with DEBUG on now prints a message instead of silently
      redirecting to the homepage.

   4. CO-BROWSE support, two changes. See the CO-BROWSE comments below.

      a. window.kioskNoteActivity() lets kiosk-cobrowse.js count an
         advisor-driven change as user activity. Without it the idle timer
         resets a screen the advisor is actively driving, which on a customer
         iPad that nobody is touching looks exactly like the sync breaking.

      b. clearKioskSession() no longer deletes the co-browse session id.
         Deleting it dropped the customer screen out of the session on the
         first idle reset, after which every advisor action went nowhere.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------------
     CONFIGURATION. Change these values and nothing else.
     --------------------------------------------------------------------- */

  var CONFIG = {
    // The token this iPad passes as ?kt=<token>. No longer a literal: it is
    // whatever layout/kiosk.liquid resolved for THIS device — that kiosk's own
    // token once provisioned, the shared fallback until then.
    //
    // A copy lived here because this gate needed one, and that is precisely how
    // the shared value ended up readable by anyone who fetched a public theme
    // asset. The layout defines window.KIOSK_TOKEN in a classic inline script
    // in the head, so it exists before this deferred file runs.
    //
    // The empty-string fallback is unreachable unless that block is removed, in
    // which case the gate fails loudly rather than quietly accepting something.
    ACCESS_TOKEN: window.KIOSK_TOKEN || '',

    // Set false to let anyone with the URL load the kiosk
    TOKEN_GATE_ENABLED: true,

    // Every kiosk page path starts with this.
    // Covers kiosk-welcome1, kiosk-advisor1, kiosk-shop1, kiosk-checkout1.
    // Any NEW kiosk page must also start with /pages/kiosk- or links to it
    // will be blocked by enforceInternalLinks().
    PATH_PREFIX: '/pages/kiosk-',

    // Where the kiosk returns to after an idle timeout
    WELCOME_PATH: '/pages/kiosk-welcome1',

    // Fallback destination for a data-kiosk-back element that has no href
    // and no history entry to go back to.
    BACK_FALLBACK_PATH: '/pages/kiosk-shop1',

    // Where a failed token gate sends the visitor
    EXIT_PATH: '/',

    // The advisor co-browse proxy. Pages inside the advisor's mirror frame are
    // served from here, and their links are rewritten to point back at it, so
    // enforceInternalLinks() has to treat it as an allowed destination. Without
    // this every link inside the mirror was blocked silently: the page looked
    // fine and clicking did nothing at all.
    MIRROR_PATH: '/apps/approvals/mirror',

    // Idle timeout in milliseconds. 120000 = 2 minutes.
    IDLE_TIMEOUT_MS: 120000,

    // Screens where the idle timer never runs. The advisor screen is here
    // because a customer may be mid-conversation with a live advisor and not
    // touching the panel. The welcome screen is already the destination.
    IDLE_EXEMPT_PATHS: [
      '/pages/kiosk-welcome1',
      '/pages/kiosk-advisor1'
    ],

    // Console logging
    DEBUG: false
  };

  var STORAGE_KEY = 'kiosk:token';
  var SESSION_PREFIX = 'kiosk:';

  /* CO-BROWSE. The session id key written by kiosk-cobrowse.js. Named here so
     clearKioskSession() can exempt it: it shares the kiosk: prefix with the
     cart and would otherwise be wiped on every idle reset. */
  var SID_KEY = 'kiosk:sid';

  /* Set for exactly one popstate by kioskBack(). Module scoped rather than on
     window, so nothing else can set it and walk out of the kiosk. */
  var intentionalBack = false;

  /* CO-BROWSE. Safe default, replaced by the real reset() inside
     startIdleTimer() when the timer actually runs. Defined here so that
     kiosk-cobrowse.js can call it unconditionally without a typeof guard
     failing on idle-exempt screens, where no timer is started at all. */
  window.kioskNoteActivity = window.kioskNoteActivity || function () {};

  function log() {
    if (!CONFIG.DEBUG || !window.console) return;
    console.log.apply(console, ['[kiosk]'].concat([].slice.call(arguments)));
  }

  function reveal() {
    document.body.classList.add('kiosk-ready');
  }

  /* ---------------------------------------------------------------------
     1. Token gate
     --------------------------------------------------------------------- */

  function readStoredToken() {
    try {
      var local = window.localStorage.getItem(STORAGE_KEY);
      if (local) return local;
    } catch (e) {
      log('localStorage read unavailable');
    }

    try {
      return window.sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
      log('sessionStorage read unavailable');
      return null;
    }
  }

  /* Returns true only if the value is genuinely retrievable afterwards.
     Private Browsing on iOS, blocked cookies and some Guided Access setups
     all throw here, and a silent failure used to strand the kiosk. */
  function storeToken(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
      return true;
    } catch (e) {
      log('localStorage write failed');
    }

    try {
      window.sessionStorage.setItem(STORAGE_KEY, value);
      log('token persisted to sessionStorage only, will not survive a tab close');
      return true;
    } catch (e) {
      log('sessionStorage write failed too, token cannot be persisted');
    }

    return false;
  }

  function passesTokenGate() {
    if (!CONFIG.TOKEN_GATE_ENABLED) return true;

    /* A provisioned iPad has already cleared this. The layout accepted a ktok
       parameter from the web clip URL and persisted it, which is the same proof
       of arrival that kt was giving, obtained one step earlier. Without this
       short circuit every web clip URL would have to carry the token twice,
       once to provision the device and once to satisfy this gate — and the two
       copies would eventually disagree.

       tokenSource is set by the KIOSK IDENTITY block in layout/kiosk.liquid.
       An unprovisioned device falls through to the kt check below, where
       CONFIG.ACCESS_TOKEN is the shared fallback, so nothing changes for a
       kiosk that has not been migrated yet. */
    if (window.KIOSK && window.KIOSK.tokenSource === 'device') return true;

    var params = new URLSearchParams(window.location.search);
    var supplied = params.get('kt');

    if (supplied === CONFIG.ACCESS_TOKEN) {
      var persisted = storeToken(supplied);

      if (persisted) {
        params.delete('kt');
        var query = params.toString();
        var clean = window.location.pathname + (query ? '?' + query : '');
        window.history.replaceState(null, '', clean);
        log('token accepted and stripped from url');
      } else {
        /* Leave kt in the URL. Ugly, but every in kiosk link is relative to
           this page, so at least the current screen keeps working instead of
           bouncing to the homepage on the next tap. */
        log('token accepted but not persisted, leaving kt in the url');
      }

      return true;
    }

    if (readStoredToken() === CONFIG.ACCESS_TOKEN) {
      return true;
    }

    log('token gate failed, exiting');
    return false;
  }

  /* ---------------------------------------------------------------------
     2. Back navigation

     Blocks browser and gesture driven back navigation, while allowing the
     kiosk's own back control through kioskBack().
     --------------------------------------------------------------------- */

  function trapBackNavigation() {
    /* An iframe's history entries join the containing page's session history,
       so pushing state from inside the co-browse frame would interfere with the
       advisor page's own navigation. The parent traps back for both. */
    if (inFrame()) {
      log('framed, back trap not installed');
      return;
    }

    try {
      window.history.pushState(null, '', window.location.href);
    } catch (e) {
      log('pushState unavailable');
      return;
    }

    window.addEventListener('popstate', function () {
      if (intentionalBack) {
        /* One sanctioned step back. Consume the flag immediately so the next
           popstate is trapped again. */
        intentionalBack = false;
        log('intentional back allowed');
        return;
      }

      window.history.pushState(null, '', window.location.href);
      log('back navigation blocked');
    });
  }

  /* The only sanctioned way backwards. Exposed on window so inline handlers
     in Liquid templates can call it. */
  function kioskBack(fallbackPath) {
    var target = fallbackPath || CONFIG.BACK_FALLBACK_PATH;

    /* history.length is 1 on a fresh load, and trapBackNavigation() pushes an
       entry, so anything at or below 2 has nowhere useful to go back to. Send
       those to the fallback screen rather than out of the kiosk. */
    if (window.history.length <= 2) {
      log('no history to go back to, using fallback', target);
      window.location.assign(target);
      return;
    }

    intentionalBack = true;
    log('going back');
    window.history.back();

    /* If the popstate never arrives, for instance because the entry we expected
       was replaced, fall back rather than leaving a dead button. */
    window.setTimeout(function () {
      if (intentionalBack) {
        intentionalBack = false;
        log('back did not fire, using fallback', target);
        window.location.assign(target);
      }
    }, 400);
  }

  window.kioskBack = kioskBack;

  /* Delegated handler, so the chevron needs no inline JavaScript.

       <a href="/pages/kiosk-shop1" data-kiosk-back>Back</a>
         -> normal navigation, handled by enforceInternalLinks(). Preferred.

       <button type="button" data-kiosk-back>Back</button>
         -> guarded history.back()

       <button type="button" data-kiosk-back="/pages/kiosk-welcome1">Back</button>
         -> guarded history.back(), falling back to that path
  */
  function wireBackControls() {
    document.addEventListener('click', function (event) {
      var el = event.target.closest ? event.target.closest('[data-kiosk-back]') : null;
      if (!el) return;

      /* An anchor with a real href is left alone: a plain navigation is more
         predictable than touching the history stack. */
      var href = el.getAttribute('href');
      if (el.tagName === 'A' && href && href !== '#') return;

      event.preventDefault();

      var explicit = el.getAttribute('data-kiosk-back');
      kioskBack(explicit && explicit !== 'true' ? explicit : null);
    });
  }

  /* ---------------------------------------------------------------------
     3. Internal only links
     --------------------------------------------------------------------- */

  function isKioskPath(pathname, search) {
    if (pathname.indexOf(CONFIG.PATH_PREFIX) === 0) return true;

    /* The advisor co-browse proxy. Links inside the mirror frame are rewritten
       to this path, and it only ever serves kiosk pages, so allowing it does
       not widen where a customer can reach. */
    if (pathname === CONFIG.MIRROR_PATH) return true;

    /* Product pages, wherever they appear in the path.

       Shopify generates collection scoped product URLs, such as
       /collections/skin-wound-care/products/kendall-alginate-dressing, which
       an "indexOf === 0" test on /products/ misses. Those were being blocked,
       and because enforceInternalLinks() calls stopPropagation() as well as
       preventDefault(), the theme's own quick view handler never ran either:
       the product link did nothing AND no modal opened. */
    if (pathname.indexOf('/products/') !== -1) return true;

    /* Any collection.

       Previously this required ?view=kiosk, which the collection template
       itself generates but its filter and sort links do not, so every filter
       control was silently dead. A collection page is not somewhere a customer
       can get lost, so the narrower rule was costing more than it bought. */
    if (pathname.indexOf('/collections/') === 0) return true;

    /* Search results, since the collection page has a search box. */
    if (pathname === '/search') return true;

    return false;
  }

  function enforceInternalLinks() {
    document.addEventListener(
      'click',
      function (event) {
        var anchor = event.target.closest ? event.target.closest('a[href]') : null;
        if (!anchor) return;

        var url;
        try {
          url = new URL(anchor.getAttribute('href'), window.location.origin);
        } catch (e) {
          event.preventDefault();
          return;
        }

        var sameOrigin = url.origin === window.location.origin;

        if (!sameOrigin || !isKioskPath(url.pathname, url.search)) {
          /* preventDefault stops the navigation, which is the point.

             stopPropagation is NOT called. It used to be, and that was a
             mistake: it stopped the theme's own click handlers from running, so
             a link the kiosk merely declines to FOLLOW also lost whatever
             in-page behaviour the theme attached to it, such as opening a quick
             view modal. Blocking navigation should not mean blocking the page. */
          event.preventDefault();
          log('blocked navigation to', url.href);
          return;
        }

        /* CO-BROWSE. An allowed link inside the advisor's mirror frame is a
           navigation the customer screen has to follow. Published here rather
           than in the template so that every kiosk screen is covered by one
           rule, including the back chevron, the pager and the subaction
           buttons, none of which the template wires up individually.

           publishNav() unwraps the mirror path and strips the host, so what
           reaches the session is a canonical storefront path. It is a no-op
           unless this document is the framed advisor view, so the customer
           screen never publishes its own applied navigations back. */
        if (window.KioskSync) {
          window.KioskSync.publishNav(anchor.getAttribute('href'));
        }
      },
      true
    );
  }

  /* ---------------------------------------------------------------------
     4. Gesture and selection suppression
     --------------------------------------------------------------------- */

  function suppressGestures() {
    ['gesturestart', 'gesturechange', 'gestureend'].forEach(function (name) {
      document.addEventListener(
        name,
        function (event) {
          event.preventDefault();
        },
        { passive: false }
      );
    });

    document.addEventListener('contextmenu', function (event) {
      event.preventDefault();
    });

    /* Form fields are exempt: blocking selectstart on an input makes it
       impossible to correct a typo in the checkout form. */
    document.addEventListener('selectstart', function (event) {
      var t = event.target;
      if (t && t.closest && t.closest('input, textarea, select, [contenteditable]')) return;
      event.preventDefault();
    });

    document.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });

    var lastTouchEnd = 0;
    document.addEventListener(
      'touchend',
      function (event) {
        var now = Date.now();
        if (now - lastTouchEnd < 300) {
          event.preventDefault();
        }
        lastTouchEnd = now;
      },
      { passive: false }
    );

    document.addEventListener(
      'touchmove',
      function (event) {
        if (event.touches.length > 1) {
          event.preventDefault();
        }
      },
      { passive: false }
    );
  }

  /* ---------------------------------------------------------------------
     5. Idle reset
     --------------------------------------------------------------------- */

  function clearKioskSession() {
    try {
      var doomed = [];
      for (var i = 0; i < window.sessionStorage.length; i += 1) {
        var key = window.sessionStorage.key(i);
        /* The token may live in sessionStorage as a fallback. Clearing it here
           would fail the gate on the very next page load.

           CO-BROWSE: the session id is exempt for the same class of reason.
           It shares the kiosk: prefix, and wiping it silently detached the
           customer screen from the advisor session on the first idle reset:
           the advisor kept publishing, the customer kept polling a session it
           no longer had an id for, and nothing on either screen said so. */
        if (key &&
            key.indexOf(SESSION_PREFIX) === 0 &&
            key !== STORAGE_KEY &&
            key !== SID_KEY) {
          doomed.push(key);
        }
      }
      doomed.forEach(function (key) {
        window.sessionStorage.removeItem(key);
      });
    } catch (e) {
      log('sessionStorage unavailable');
    }
  }

  /* True when this document is inside the advisor co-browse iframe rather than
     being the kiosk screen itself. Several behaviours below are wrong in that
     context: the framed page is not the thing the customer is looking at as a
     whole, and it must not try to manage navigation for the page that contains
     it. */
  function inFrame() {
    try {
      return window.self !== window.top;
    } catch (e) {
      /* A cross origin container throws on access, which also means framed. */
      return true;
    }
  }

  function isIdleExempt() {
    /* Inside the co-browse frame the customer may be talking to an advisor for
       several minutes without touching anything. Resetting to the welcome
       screen mid conversation would be worse than useless, and the containing
       advisor page runs its own exemption anyway. */
    if (inFrame()) return true;

    var path = window.location.pathname;
    for (var i = 0; i < CONFIG.IDLE_EXEMPT_PATHS.length; i += 1) {
      if (path === CONFIG.IDLE_EXEMPT_PATHS[i]) return true;
    }

    /* Fallback in case a page handle changes: the advisor template marks
       itself with data-kiosk-screen="advisor". */
    var screenEl = document.querySelector('[data-kiosk-screen]');
    if (screenEl && screenEl.getAttribute('data-kiosk-screen') === 'advisor') {
      return true;
    }

    return false;
  }

  function startIdleTimer() {
    if (isIdleExempt()) {
      log('idle timer not started for', window.location.pathname);
      return;
    }

    var timer = null;

    function reset() {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        log('idle timeout, returning to welcome');
        clearKioskSession();
        window.location.replace(CONFIG.WELCOME_PATH);
      }, CONFIG.IDLE_TIMEOUT_MS);
    }

    /* CO-BROWSE. An advisor-driven change fires no touch, click or input event
       on the customer screen, so without this the timer runs to zero while the
       advisor is actively working and the screen jumps to welcome. On a
       customer iPad that nobody is touching, that is indistinguishable from
       the sync having died. kiosk-cobrowse.js calls this before applying every
       event and before every pushed navigation. */
    window.kioskNoteActivity = reset;

    /* input and change are included so that typing into the checkout form,
       or picking a province, counts as activity. */
    [
      'touchstart',
      'touchend',
      'click',
      'keydown',
      'pointerdown',
      'scroll',
      'input',
      'change'
    ].forEach(function (name) {
      document.addEventListener(name, reset, { passive: true });
    });

    reset();
    log('idle timer started,', CONFIG.IDLE_TIMEOUT_MS, 'ms');
  }

  /* ---------------------------------------------------------------------
     6. Setup hint
     --------------------------------------------------------------------- */

  function maybeShowSetupNote() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('setup') !== '1') return;

    var standalone =
      window.navigator.standalone === true ||
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);

    if (standalone) return;

    var note = document.getElementById('kiosk-setup-note');
    if (!note) return;

    var closer = note.querySelector('[data-kiosk-dismiss-setup]');
    if (closer) {
      closer.addEventListener('click', function () {
        note.hidden = true;
      });
    }

    note.hidden = false;
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */

  function init() {
    if (!passesTokenGate()) {
      /* A silent redirect to the homepage is the worst possible diagnostic:
         it looks identical to a theme problem. With DEBUG on, say what
         happened instead. */
      if (CONFIG.DEBUG) {
        document.body.innerHTML =
          '<pre style="padding:2rem;font:16px/1.6 monospace">' +
          'Kiosk token gate failed.\n\n' +
          'Open this page once with ?kid=kiosk-N&amp;ktok=&lt;device token&gt;\n' +
          'to provision this iPad, or set TOKEN_GATE_ENABLED to false in\n' +
          'assets/kiosk.js.' +
          '</pre>';
        return;
      }

      /* Inside the advisor co-browse frame, redirecting to EXIT_PATH sent the
         frame to the storefront homepage, which refuses to be framed: the
         advisor saw a broken frame icon and an empty console. Say so in place
         instead, since the container cannot be navigated from here anyway. */
      if (inFrame()) {
        log('framed and gate failed, not redirecting');
        document.body.innerHTML =
          '<div style="padding:2rem;font:15px/1.6 system-ui,sans-serif;color:#4E5668">' +
          'This panel could not load. Open the advisor dashboard again, or ' +
          'check that this iPad is provisioned: window.KIOSK.tokenSource ' +
          'should read "device", not "shared".' +
          '</div>';
        return;
      }

      window.location.replace(CONFIG.EXIT_PATH);
      return;
    }

    reveal();
    trapBackNavigation();
    wireBackControls();
    enforceInternalLinks();
    suppressGestures();
    startIdleTimer();
    maybeShowSetupNote();

    log('kiosk ready at', window.location.pathname);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();